import { useTranslation } from "react-i18next";
import { Icon } from "@majourneys/icons";
import { ENGRAVED, TrailingFade } from "./engraved";

export interface FactTilesProps {
  facts: readonly string[] | null | undefined;
  /** Tiles shown at most. */
  max?: number;
  className?: string;
}

/**
 * Keyword match, first rule wins. Facts are editorial data already localized
 * by the API, so the words cover en and fr (the bulk of the source copy);
 * anything unmatched gets the sparkle rather than a wrong pictogram.
 */
const ICON_RULES: Array<{ test: RegExp; icon: string }> = [
  { test: /\b(open|ouvert|hours?|horaires?|daily|tous les jours)\b|\d{1,2}:\d{2}/i, icon: "ri-time-line" },
  { test: /\b(mad|dh|dhs|price|prix|€|\$)\b|\bmains?\b/i, icon: "ri-money-dollar-circle-line" },
  { test: /\b(dj|show|live|music|musique|concert)\b/i, icon: "ri-music-2-line" },
  { test: /\b(lounge|level|floor|rooftop|terrace|terrasse|étage|niveaux?)\b/i, icon: "ri-building-line" },
  {
    test: /\b(tapas|cuisine|mediterranean|méditerranéen\w*|moroccan|marocain\w*|italian|italien\w*|french|sushi|grill|food|restaurant)\b/i,
    icon: "ri-restaurant-line",
  },
  {
    test: /\b(rue|street|avenue|av\.?|gu[eé]liz|medina|médina|hivernage|district|quartier|road|place)\b/i,
    icon: "ri-map-pin-line",
  },
];

export function factIcon(fact: string): string {
  return ICON_RULES.find((r) => r.test.test(fact))?.icon ?? "ri-sparkling-2-line";
}

/**
 * The venue's short facts as a sideways row of engraved tiles.
 *
 * The row bleeds to the screen edge on a phone (-mx-4 against the page
 * Container's px-4) so the trailing tile visibly runs off, and carries
 * -my-3 py-3: a scroll container clips on both axes, and without that room
 * the tiles' shadows would be shaved into a hard grey edge.
 */
export function FactTiles({ facts, max = 6, className }: FactTilesProps) {
  const { t } = useTranslation();
  const shown = (facts ?? []).filter((f) => typeof f === "string" && f.trim()).slice(0, max);
  if (shown.length === 0) return null;

  return (
    <section
      aria-label={t("activity.restaurant.key_facts")}
      className={`relative -mx-4 lg:mx-0 ${className ?? ""}`}
    >
      <ul className="-my-3 flex gap-2 overflow-x-auto px-4 py-3 [scrollbar-width:none] lg:px-0 [&::-webkit-scrollbar]:hidden">
        {shown.map((fact) => (
          <li key={fact} className="shrink-0">
            <div
              className="flex min-h-[44px] max-w-[200px] items-center gap-2 rounded-2xl py-2 pe-3 ps-2.5"
              style={ENGRAVED}
            >
              <Icon name={factIcon(fact)} className="shrink-0 text-[16px] text-[#110E39]" aria-hidden="true" />
              <span className="min-w-0 font-sans text-[12.5px] font-semibold leading-[16px] text-[#110E39]">
                {fact}
              </span>
            </div>
          </li>
        ))}
        {/* Spacer so the last tile can scroll clear of the fade. */}
        <li aria-hidden="true" className="w-6 shrink-0" />
      </ul>
      <TrailingFade />
    </section>
  );
}
